import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per football match
 * {
 *   matchType: "regular" | "friendly" | "tournament",
 *   duration: number,
 *   userTeamName: string,
 *   opponentTeamName: string,
 *   userTeam: [{ id, userId, name, isCustom }],
 *   opponentTeam: [{ id, userId, name, isCustom }],
 *   goalEvents: [
 *     { id, scorer, scorerId, assist, assistId, minute, team: "user" | "opponent" }
 *   ],
 *   penaltyEvents: [
 *     { id, scorer, scorerId, team: "user" | "opponent", scored: boolean }
 *   ],
 *   hasPenalties: boolean,
 *   winner: "user" | "opponent" | null,
 *   userGoals: number,
 *   opponentGoals: number,
 *   userPenalties: number,
 *   opponentPenalties: number
 * }
 */

export const AddFootball = () => {
  const location = useLocation();
  const data = location.state || {};

  const [footballMatches, setFootballMatches] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  const validateMatches = () => {
    const errors = [];

    footballMatches.forEach((match, index) => {
      // Check team names
      if (!match.userTeamName || !match.opponentTeamName) {
        errors.push(`Match ${index + 1}: Team names are required`);
      }

      // Check if teams have players
      if (match.userTeam.length === 0 || match.opponentTeam.length === 0) {
        errors.push(
          `Match ${index + 1}: Each team must have at least one player`
        );
      }

      // Check match duration
      if (match.duration < 45 || match.duration > 120) {
        errors.push(
          `Match ${index + 1}: Match duration should be between 45-120 minutes`
        );
      }

      // Validate goal events
      match.goalEvents.forEach((goal, goalIndex) => {
        if (!goal.scorer || !goal.minute) {
          errors.push(
            `Match ${index + 1}, Goal ${
              goalIndex + 1
            }: Scorer and minute are required`
          );
        }

        // Check if minute is within match duration
        if (goal.minute > match.duration) {
          errors.push(
            `Match ${index + 1}, Goal ${goalIndex + 1}: Goal minute (${
              goal.minute
            }) cannot exceed match duration (${match.duration})`
          );
        }

        // Check if scorer belongs to the correct team
        const team = goal.team === "user" ? match.userTeam : match.opponentTeam;
        const scorerExists = team.some((player) => player.name === goal.scorer);
        if (!scorerExists && goal.scorer !== "Guest Player") {
          errors.push(
            `Match ${index + 1}, Goal ${goalIndex + 1}: Scorer "${
              goal.scorer
            }" is not in the ${goal.team === "user" ? "your" : "opponent"} team`
          );
        }
      });

      // Validate penalty events
      match.penaltyEvents.forEach((penalty, penIndex) => {
        if (!penalty.scorer) {
          errors.push(
            `Match ${index + 1}, Penalty ${penIndex + 1}: Scorer is required`
          );
        }

        // Check if penalty scorer belongs to the correct team
        const team =
          penalty.team === "user" ? match.userTeam : match.opponentTeam;
        const scorerExists = team.some(
          (player) => player.name === penalty.scorer
        );
        if (!scorerExists && penalty.scorer !== "Guest Player") {
          errors.push(
            `Match ${index + 1}, Penalty ${penIndex + 1}: Scorer "${
              penalty.scorer
            }" is not in the ${
              penalty.team === "user" ? "your" : "opponent"
            } team`
          );
        }
      });

      // Check if match has any scoring activity
      if (match.goalEvents.length === 0 && match.penaltyEvents.length === 0) {
        errors.push(
          `Match ${
            index + 1
          }: Match should have at least some scoring events (goals or penalties)`
        );
      }
    });

    return errors;
  };

  const handleSaveAll = async () => {
    const validationErrors = validateMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: footballMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all football matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  const handleAddFootballMatch = () => {
    const newMatch = {
      matchType: "regular",
      duration: 90,
      userTeamName: "",
      opponentTeamName: "",
      userTeam: [], // Array of player objects
      opponentTeam: [], // Array of player objects
      goalEvents: [], // Array of goal events with details
      penaltyEvents: [], // Array of penalty events
      hasPenalties: false,
      winner: null,
      userGoals: 0,
      opponentGoals: 0,
      userPenalties: 0,
      opponentPenalties: 0,
    };
    setFootballMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setFootballMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setFootballMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className="max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div
          className="border-b-2 pb-4 mb-6"
          style={{ borderColor: "#3498db" }}
        >
          <h2 className="text-3xl font-semibold text-gray-800 mb-2">
            {data.sport} Matches at {data.arenaBooking?.name}
          </h2>
          <p className="text-lg text-gray-600">
            {data.arenaBooking?.date && data.arenaBooking?.time ? (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            ) : (
              "Add detailed football match information"
            )}
          </p>
        </div>

        <button
          className="bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5"
          onClick={handleAddFootballMatch}
        >
          Add New Football Match
        </button>

        {footballMatches.length === 0 && (
          <div className="text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg">
            No matches added yet. Click the button above to add your first
            football match.
          </div>
        )}

        {footballMatches.map((match, index) => (
          <FootballMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            isMobile={isMobile}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {footballMatches.length > 0 && (
          <button
            onClick={handleSaveAll}
            className="w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5"
          >
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const FootballMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  isMobile,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);
  const [activeTab, setActiveTab] = useState("basic"); // basic, teams, events

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    tabContainer: "flex mb-5 border-b border-gray-200 flex-wrap",
    tab: "py-2 px-4 text-sm sm:text-base font-medium border-b-2 border-transparent bg-transparent transition-colors",
    teamSection: "bg-white p-5 rounded-lg shadow-sm mb-5",
    formGroup: "space-y-4",
    label: "font-medium text-gray-700",
    input:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    deleteButton: "bg-red-500 text-white px-2 py-1 rounded text-xs",
    eventCard:
      "bg-white p-4 rounded-lg mb-3 border border-gray-200 grid gap-3 items-center" +
      (isMobile ? " grid-cols-1" : " grid-cols-[120px_1fr_1fr_80px_auto]"),
    playerCard:
      "bg-gray-50 p-3 rounded-lg mb-3 flex justify-between items-center",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox: "text-center p-4 rounded-lg bg-blue-50 min-w-[120px]",
  };

  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const tabBaseStyle = {
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "16px",
    color: "#718096",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const tabActiveStyle = {
    color: brandBlue,
    borderBottomColor: brandBlue,
  };

  const addPlayer = (team, userInfo, customName = "") => {
    // If it's a system user (not custom), check if already added to any team
    if (userInfo && userInfo.id) {
      const isInUserTeam = localMatch.userTeam.some(
        (player) => player.userId === userInfo.id
      );
      const isInOpponentTeam = localMatch.opponentTeam.some(
        (player) => player.userId === userInfo.id
      );

      if (isInUserTeam || isInOpponentTeam) {
        toast.error(
          `${userInfo.full_name || userInfo.username} is already added to ${
            isInUserTeam ? "your team" : "opponent team"
          }`
        );
        return;
      }
    }

    const updated = { ...localMatch };
    const player = {
      id: userInfo?.id || `custom_${Date.now()}`,
      userId: userInfo?.id || null, // Store actual user ID for statistics
      name: userInfo?.full_name || customName || "Guest Player",
      isCustom: !userInfo,
    };

    updated[team].push(player);
    updateParent(updated);
  };

  const removePlayer = (team, playerIndex) => {
    const updated = { ...localMatch };
    updated[team].splice(playerIndex, 1);
    updateParent(updated);
  };

  const addGoalEvent = () => {
    const updated = { ...localMatch };
    updated.goalEvents.push({
      id: Date.now(),
      scorer: "",
      scorerId: null, // Store user ID for statistics
      assist: "",
      assistId: null, // Store user ID for assists
      minute: "",
      team: "user", // user or opponent
    });
    updateParent(updated);
  };

  const updateGoalEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.goalEvents[index][field] = value;

    // If updating scorer, also update scorerId
    if (field === "scorer" && value) {
      const selectedPlayer = localMatch[
        updated.goalEvents[index].team === "user" ? "userTeam" : "opponentTeam"
      ].find((player) => player.name === value);
      updated.goalEvents[index].scorerId = selectedPlayer?.userId || null;
    }

    // If updating assist, also update assistId
    if (field === "assist" && value) {
      const selectedPlayer = localMatch[
        updated.goalEvents[index].team === "user" ? "userTeam" : "opponentTeam"
      ].find((player) => player.name === value);
      updated.goalEvents[index].assistId = selectedPlayer?.userId || null;
    }

    recalculateScores(updated);
  };

  const removeGoalEvent = (index) => {
    const updated = { ...localMatch };
    updated.goalEvents.splice(index, 1);
    recalculateScores(updated);
  };

  const addPenaltyEvent = () => {
    const updated = { ...localMatch };
    updated.penaltyEvents.push({
      id: Date.now(),
      scorer: "",
      scorerId: null, // Store user ID for statistics
      team: "user", // user or opponent
      scored: true,
    });
    updateParent(updated);
  };

  const updatePenaltyEvent = (index, field, value) => {
    const updated = { ...localMatch };
    updated.penaltyEvents[index][field] = value;

    // If updating scorer, also update scorerId
    if (field === "scorer" && value) {
      const selectedPlayer = localMatch[
        updated.penaltyEvents[index].team === "user"
          ? "userTeam"
          : "opponentTeam"
      ].find((player) => player.name === value);
      updated.penaltyEvents[index].scorerId = selectedPlayer?.userId || null;
    }

    recalculateScores(updated);
  };

  const removePenaltyEvent = (index) => {
    const updated = { ...localMatch };
    updated.penaltyEvents.splice(index, 1);
    recalculateScores(updated);
  };

  const recalculateScores = (updatedMatch) => {
    let userGoals = 0;
    let opponentGoals = 0;
    let userPenalties = 0;
    let opponentPenalties = 0;

    // Count goals
    updatedMatch.goalEvents.forEach((goal) => {
      if (goal.team === "user") userGoals++;
      else opponentGoals++;
    });

    // Count successful penalties
    updatedMatch.penaltyEvents.forEach((penalty) => {
      if (penalty.scored) {
        if (penalty.team === "user") userPenalties++;
        else opponentPenalties++;
      }
    });

    updatedMatch.userGoals = userGoals;
    updatedMatch.opponentGoals = opponentGoals;
    updatedMatch.userPenalties = userPenalties;
    updatedMatch.opponentPenalties = opponentPenalties;

    // Determine winner
    const totalUserScore = userGoals + userPenalties;
    const totalOpponentScore = opponentGoals + opponentPenalties;

    if (totalUserScore > totalOpponentScore) {
      updatedMatch.winner = "user";
    } else if (totalOpponentScore > totalUserScore) {
      updatedMatch.winner = "opponent";
    } else {
      updatedMatch.winner = null;
    }

    updateParent(updatedMatch);
  };

  const renderBasicInfo = () => (
    <div>
      {/* Football Scoring Guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5">
        <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
          ⚽ Football Match Guide
        </h4>
        <div className="text-blue-700 space-y-2 text-sm leading-relaxed">
          <p>
            <strong>Match Types:</strong> Regular, Friendly, Tournament
          </p>
          <p>
            <strong>Team Setup:</strong> At least 7 players (11 recommended)
          </p>
          <p>
            <strong>Duration:</strong> 45-120 minutes (standard 90)
          </p>
          <p>
            <strong>Scoring:</strong> Goals with scorer/assist + minute
          </p>
          <p>
            <strong>Penalties:</strong> Track shootout separately if needed
          </p>
          <p>
            <strong>Winner:</strong> Most goals + penalty goals wins
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Your Team Name</label>
            <input
              type="text"
              value={localMatch.userTeamName}
              onChange={(e) =>
                updateParent({ ...localMatch, userTeamName: e.target.value })
              }
              className={formClasses.input}
              placeholder="Enter your team name"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Opponent Team Name</label>
            <input
              type="text"
              value={localMatch.opponentTeamName}
              onChange={(e) =>
                updateParent({
                  ...localMatch,
                  opponentTeamName: e.target.value,
                })
              }
              className={formClasses.input}
              placeholder="Enter opponent team name"
            />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mt-3">
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Match Type</label>
            <select
              value={localMatch.matchType}
              onChange={(e) =>
                updateParent({ ...localMatch, matchType: e.target.value })
              }
              className={`${formClasses.select} w-full`}
            >
              <option value="regular">Regular</option>
              <option value="friendly">Friendly</option>
              <option value="tournament">Tournament</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className={formClasses.label}>Duration (minutes)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={localMatch.duration}
                onChange={(e) => {
                  const duration = Math.max(
                    45,
                    Math.min(120, Number(e.target.value))
                  );
                  updateParent({ ...localMatch, duration });
                }}
                className={`${formClasses.input} max-w-[120px]`}
                min="45"
                max="120"
              />
              <span className="text-xs text-gray-500">(45-120 min)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeamManagement = () => (
    <div>
      {/* User Team */}
      <div className={formClasses.teamSection}>
        <h4>Your Team: {localMatch.userTeamName || "Unnamed Team"}</h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("userTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.userTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                {player.name} {player.isCustom && "(Custom)"}
              </span>
              <button
                onClick={() => removePlayer("userTeam", index)}
                className={formClasses.deleteButton}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Opponent Team */}
      <div className={formClasses.teamSection}>
        <h4>Opponent Team: {localMatch.opponentTeamName || "Unnamed Team"}</h4>
        <PlayerSelector
          availableUsers={availableUsers}
          onAddPlayer={(userInfo, customName) =>
            addPlayer("opponentTeam", userInfo, customName)
          }
          selectedUserIds={[
            ...localMatch.userTeam.filter((p) => p.userId).map((p) => p.userId),
            ...localMatch.opponentTeam
              .filter((p) => p.userId)
              .map((p) => p.userId),
          ]}
        />
        <div className="mt-4 space-y-3">
          {localMatch.opponentTeam.map((player, index) => (
            <div key={player.id} className={formClasses.playerCard}>
              <span className="font-medium text-gray-800">
                {player.name} {player.isCustom && "(Custom)"}
              </span>
              <button
                onClick={() => removePlayer("opponentTeam", index)}
                className={formClasses.deleteButton}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEvents = () => (
    <div>
      {/* Goals Section */}
      <div className={formClasses.teamSection}>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-gray-800">Goals</h4>
          <button onClick={addGoalEvent} className={formClasses.addButton}>
            Add Goal
          </button>
        </div>
        {localMatch.goalEvents.map((goal, index) => (
          <div key={goal.id} className={formClasses.eventCard}>
            <select
              value={goal.team}
              onChange={(e) => updateGoalEvent(index, "team", e.target.value)}
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent Team"}
              </option>
            </select>
            <select
              value={goal.scorer}
              onChange={(e) => updateGoalEvent(index, "scorer", e.target.value)}
              className={formClasses.select}
            >
              <option value="">Select scorer</option>
              {goal.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={goal.assist}
              onChange={(e) => updateGoalEvent(index, "assist", e.target.value)}
              className={formClasses.select}
            >
              <option value="">Select assist (optional)</option>
              {goal.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <input
              type="number"
              value={goal.minute}
              onChange={(e) => {
                const minute = Math.max(
                  1,
                  Math.min(localMatch.duration, Number(e.target.value))
                );
                updateGoalEvent(index, "minute", minute);
              }}
              placeholder="Minute"
              className="w-20 px-3 py-2 border border-gray-300 rounded text-center"
              min="1"
              max={localMatch.duration}
            />
            <button
              onClick={() => removeGoalEvent(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Penalties Section */}
      <div className={formClasses.teamSection}>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-gray-800">
            Penalty Shootout
          </h4>
          <button onClick={addPenaltyEvent} className={formClasses.addButton}>
            Add Penalty
          </button>
        </div>
        {localMatch.penaltyEvents.map((penalty, index) => (
          <div key={penalty.id} className={formClasses.eventCard}>
            <select
              value={penalty.team}
              onChange={(e) =>
                updatePenaltyEvent(index, "team", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="user">
                {localMatch.userTeamName || "Your Team"}
              </option>
              <option value="opponent">
                {localMatch.opponentTeamName || "Opponent Team"}
              </option>
            </select>
            <select
              value={penalty.scorer}
              onChange={(e) =>
                updatePenaltyEvent(index, "scorer", e.target.value)
              }
              className={formClasses.select}
            >
              <option value="">Select player</option>
              {penalty.team === "user" ? (
                localMatch.userTeam.length > 0 ? (
                  localMatch.userTeam.map((player) => (
                    <option key={player.id} value={player.name}>
                      {player.name}
                    </option>
                  ))
                ) : (
                  <option value="Guest Player">Guest Player</option>
                )
              ) : localMatch.opponentTeam.length > 0 ? (
                localMatch.opponentTeam.map((player) => (
                  <option key={player.id} value={player.name}>
                    {player.name}
                  </option>
                ))
              ) : (
                <option value="Guest Player">Guest Player</option>
              )}
            </select>
            <select
              value={penalty.scored}
              onChange={(e) =>
                updatePenaltyEvent(index, "scored", e.target.value === "true")
              }
              className={formClasses.select}
            >
              <option value={true}>Scored</option>
              <option value={false}>Missed</option>
            </select>
            <button
              onClick={() => removePenaltyEvent(index)}
              className={formClasses.deleteButton}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Football Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      <div className={formClasses.tabContainer}>
        {["basic", "teams", "events"].map((tab) => (
          <button
            key={tab}
            className={formClasses.tab}
            style={{
              ...tabBaseStyle,
              ...(activeTab === tab ? tabActiveStyle : {}),
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "basic"
              ? "Basic Info"
              : tab === "teams"
              ? "Teams"
              : "Events"}
          </button>
        ))}
      </div>

      {activeTab === "basic" && renderBasicInfo()}
      {activeTab === "teams" && renderTeamManagement()}
      {activeTab === "events" && renderEvents()}

      {/* Score Summary */}
      <div className={formClasses.scoreCard}>
        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.userTeamName || "Your Team"}
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.userGoals + localMatch.userPenalties}
          </div>
          <div className="text-xs text-gray-500">
            Goals: {localMatch.userGoals} | Penalties:{" "}
            {localMatch.userPenalties}
          </div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className={formClasses.scoreLabel}>Result</div>
          <div className="text-base font-semibold text-blue-700">
            {localMatch.winner === null
              ? "Draw"
              : localMatch.winner === "user"
              ? "Win"
              : "Loss"}
          </div>
        </div>

        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localMatch.opponentTeamName || "Opponent"}
          </div>
          <div className={formClasses.scoreValue}>
            {localMatch.opponentGoals + localMatch.opponentPenalties}
          </div>
          <div className="text-xs text-gray-500">
            Goals: {localMatch.opponentGoals} | Penalties:{" "}
            {localMatch.opponentPenalties}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayerSelector = ({
  availableUsers,
  onAddPlayer,
  selectedUserIds = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (useCustomName && customName.trim()) {
      onAddPlayer(null, customName.trim());
      setCustomName("");
    } else if (!useCustomName && selectedUser) {
      onAddPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
    }
  };

  return (
    <div className="flex gap-2.5 items-center flex-wrap relative">
      {!useCustomName ? (
        <>
          <div className="relative flex-1 min-w-[250px]">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search for system users..."
              className="p-2 rounded border border-[#cbd5e0] text-sm w-full"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-[#cbd5e0] border-t-0 rounded-b max-h-[200px] overflow-y-auto z-[1000] shadow-[0_4px_6px_rgba(0,0,0,0.1)]">
                {suggestions.map((user) => {
                  const isAlreadySelected = selectedUserIds.includes(
                    user.id || user.user_id
                  );
                  return (
                    <div
                      key={user.id || user.user_id}
                      className={`p-2 px-3 cursor-pointer border-b border-[#f0f0f0] text-sm transition-colors ${
                        isAlreadySelected
                          ? "opacity-50 cursor-not-allowed bg-[#f0f0f0]"
                          : "bg-white hover:bg-[#f7fafc]"
                      }`}
                      onClick={() =>
                        !isAlreadySelected && handleSelectUser(user)
                      }
                    >
                      <div className="font-medium">
                        {user.full_name || user.username}
                        {isAlreadySelected && (
                          <span className="text-[#e53e3e] ml-2">
                            (Already Added)
                          </span>
                        )}
                      </div>
                      {user.email && (
                        <div className="text-xs text-[#718096]">
                          {user.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {showSuggestions &&
              searchTerm.length >= 2 &&
              suggestions.length === 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-[#cbd5e0] border-t-0 rounded-b max-h-[200px] overflow-y-auto z-[1000] shadow-[0_4px_6px_rgba(0,0,0,0.1)]">
                  <div className="p-2 px-3 text-sm text-[#718096] text-center">
                    No users found
                  </div>
                </div>
              )}
          </div>
          <button
            onClick={handleAdd}
            disabled={!selectedUser}
            className="bg-[#3498db] text-white border-none py-2 px-3 rounded text-sm cursor-pointer disabled:opacity-50"
          >
            Add Player
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Enter custom player name"
            className="p-2 rounded border border-[#cbd5e0] text-sm w-full"
          />
          <button
            onClick={handleAdd}
            disabled={!customName.trim()}
            className="bg-[#3498db] text-white border-none py-2 px-3 rounded text-sm cursor-pointer disabled:opacity-50"
          >
            Add Player
          </button>
        </>
      )}
      <span
        onClick={() => setUseCustomName(!useCustomName)}
        className="text-xs text-[#3498db] cursor-pointer underline"
      >
        {useCustomName ? "Use system users" : "Add custom name"}
      </span>
    </div>
  );
};

export default AddFootball;
